import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Bell, Loader2, RotateCcw, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";

const NAVY = "#0B1B33";
const VIEW = 280;
const SHAPES = { circle: "50%", squircle: "30%", rounded: "18%" };

/** Square crop for non-square images: drag to move, slider to zoom. Returns a 1024 px PNG blob via ref.current(). */
const Cropper = ({ src, nat, exportRef }) => {
  const [zoom, setZoom] = useState(1);
  const [off, setOff] = useState({ x: 0, y: 0 });
  const drag = useRef(null);
  const scale = (VIEW / Math.min(nat.w, nat.h)) * zoom;
  const w = nat.w * scale, h = nat.h * scale;
  const clamp = (o) => ({ x: Math.min(0, Math.max(VIEW - w, o.x)), y: Math.min(0, Math.max(VIEW - h, o.y)) });
  useEffect(() => { setOff(clamp({ x: (VIEW - w) / 2, y: (VIEW - h) / 2 })); }, [zoom, nat.w, nat.h]); // eslint-disable-line react-hooks/exhaustive-deps
  exportRef.current = () => new Promise((res) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas"); c.width = c.height = 1024;
      c.getContext("2d").drawImage(img, -off.x / scale, -off.y / scale, VIEW / scale, VIEW / scale, 0, 0, 1024, 1024);
      c.toBlob(res, "image/png");
    };
    img.src = src;
  });
  return (
    <div className="space-y-2" data-testid="app-icon-cropper">
      <div className="relative touch-none overflow-hidden rounded-lg bg-[repeating-conic-gradient(#e2e8f0_0%_25%,#fff_0%_50%)] bg-[length:16px_16px]" style={{ width: VIEW, height: VIEW }}
        onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); drag.current = { sx: e.clientX, sy: e.clientY, o: off }; }}
        onPointerMove={(e) => drag.current && setOff(clamp({ x: drag.current.o.x + e.clientX - drag.current.sx, y: drag.current.o.y + e.clientY - drag.current.sy }))}
        onPointerUp={() => { drag.current = null; }}>
        <img src={src} alt="" draggable={false} className="pointer-events-none absolute max-w-none select-none" style={{ width: w, height: h, left: off.x, top: off.y }} />
        <div className="pointer-events-none absolute inset-0 ring-2 ring-inset ring-teal-500" />
      </div>
      <div className="flex items-center gap-3" style={{ width: VIEW }}><span className="text-xs text-slate-500">Zoom</span><Slider min={1} max={4} step={0.05} value={[zoom]} onValueChange={([z]) => setZoom(z)} data-testid="app-icon-zoom" /></div>
      <p className="text-xs text-slate-500">Drag to position the square crop.</p>
    </div>
  );
};

const Phone = ({ img, shape, dark, ios, name }) => (
  <div className={cn("flex w-32 flex-col items-center gap-1.5 rounded-2xl p-4", dark ? "bg-slate-900" : "bg-gradient-to-br from-sky-100 to-indigo-100")}>
    <img src={img} alt="" className="h-14 w-14 shadow-md" style={{ borderRadius: ios ? "22%" : SHAPES[shape] }} />
    <span className={cn("max-w-full truncate text-[11px] font-medium", dark ? "text-white" : "text-slate-800")}>{name}</span>
  </div>
);

const Previews = ({ p, shape, name }) => (
  <div className="space-y-5" data-testid="app-icon-previews">
    <div>
      <Label className="text-xs text-slate-500">Browser tab</Label>
      <div className="mt-1.5 inline-flex items-center gap-2 rounded-t-lg border border-b-0 border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 shadow-sm" data-testid="preview-tab">
        <img src={p.images["favicon-16.png"]} alt="" className="h-4 w-4" />{name} · Dashboard<span className="ml-4 text-slate-300">✕</span>
      </div>
      <div className="mt-2 flex items-center gap-3 text-xs text-slate-500"><img src={p.images["favicon-16.png"]} alt="" className="h-4 w-4 [image-rendering:pixelated]" /> 16 px <img src={p.images["favicon-32.png"]} alt="" className="h-8 w-8" /> 32 px</div>
    </div>
    <div>
      <Label className="text-xs text-slate-500">Home screen (iOS rounded square · Android {shape})</Label>
      <div className="mt-1.5 flex flex-wrap gap-3" data-testid="preview-phones">
        <Phone img={p.images["apple-touch-icon-180.png"]} ios name={name} />
        <Phone img={p.images["apple-touch-icon-180.png"]} ios dark name={name} />
        <Phone img={p.images["maskable-192.png"]} shape={shape} name={name} />
        <Phone img={p.images["maskable-192.png"]} shape={shape} dark name={name} />
      </div>
    </div>
    <div>
      <Label className="text-xs text-slate-500">Notification</Label>
      <div className="mt-1.5 flex max-w-sm items-start gap-3 rounded-xl bg-slate-800 p-3 text-white shadow" data-testid="preview-notification">
        <img src={p.images["icon-192.png"]} alt="" className="h-9 w-9 rounded-lg" />
        <div className="min-w-0 flex-1"><div className="flex items-center gap-1.5 text-[11px] text-slate-300"><img src={p.images["badge-96.png"]} alt="" className="h-3.5 w-3.5" /> {name} · now</div>
          <div className="text-sm font-medium">Approval request approved</div><div className="truncate text-xs text-slate-300">Approved · Code 482 913</div></div>
        <Bell className="h-4 w-4 text-slate-400" />
      </div>
    </div>
  </div>
);

/** Super admin: one global app icon (favicon, PWA, iOS, notification badge) with preview, publish, roll back and reset. */
export default function AppIconPage() {
  const { user } = useAuth();
  const [st, setSt] = useState(null);
  const [file, setFile] = useState(null);
  const [src, setSrc] = useState(null);
  const [nat, setNat] = useState(null);
  const [removeBg, setRemoveBg] = useState(false);
  const [bgMode, setBgMode] = useState("white");
  const [custom, setCustom] = useState("#0D9488");
  const [padding, setPadding] = useState(12);
  const [shape, setShape] = useState("circle");
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState("");
  const crop = useRef(null);
  const load = () => api.get("/super/app-icon").then((r) => setSt(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { if (user?.is_super_admin) load(); }, [user]);
  const bg = { white: "#FFFFFF", navy: NAVY, suggested: p?.suggested || NAVY, custom }[bgMode];
  const square = nat && Math.abs(nat.w - nat.h) <= 2;
  const pick = (f) => {
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("The image must be 5 MB or smaller"); return; }
    setFile(f); setP(null);
    const url = URL.createObjectURL(f); setSrc(url);
    const im = new Image(); im.onload = () => setNat({ w: im.naturalWidth || 1024, h: im.naturalHeight || 1024 }); im.src = url;
  };
  const form = async () => {
    const fd = new FormData();
    fd.append("file", square || !crop.current ? file : new File([await crop.current()], "icon.png", { type: "image/png" }));
    fd.append("remove_bg", removeBg); fd.append("bg", bg); fd.append("padding", padding);
    return fd;
  };
  const run = async (key, fn) => { setBusy(key); try { await fn(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); } };
  const preview = () => run("preview", async () => setP((await api.post("/super/app-icon/preview", await form())).data));
  const publish = () => window.confirm("Publish this icon for the whole app and every company?") && run("publish", async () => { await api.post("/super/app-icon/publish", await form()); toast.success("Published · reload to see the new tab icon"); load(); });
  const activate = (v) => window.confirm(v == null ? "Reset to the default brain icon?" : `Roll back to version ${v}?`) && run(`v${v}`, async () => { await api.post("/super/app-icon/activate", { v }); toast.success(v == null ? "Reset to the brain icon" : `Version ${v} is live`); load(); });

  if (!user?.is_super_admin) return <p className="text-sm text-slate-500">Only the super admin can change the app icon.</p>;
  return (
    <div className="mx-auto max-w-[1200px] space-y-6" data-testid="app-icon-page">
      <PageHeader eyebrow="Platform" title="App icon" subtitle="One icon for the whole app and every company: browser tab, installed app, iPhone Home Screen and notifications." />
      <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="app-icon-note">Installed apps on iPhone update their icon only after being re-added to the Home Screen; Android updates within ~24 h.</p>
      <div className="grid gap-6 lg:grid-cols-[340px_minmax(0,1fr)]">
        <section className="space-y-4 rounded-lg border border-slate-200 bg-white p-4 sm:p-5">
          <label className="flex min-h-[44px] cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 px-3 py-4 text-sm text-slate-600 hover:border-teal-500" data-testid="app-icon-upload">
            <Upload className="h-4 w-4" /> {file ? file.name : "Choose PNG, SVG, JPG or WEBP (≤ 5 MB, 512×512+)"}
            <input type="file" accept="image/png,image/svg+xml,image/jpeg,image/webp" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="app-icon-file" />
          </label>
          {nat && Math.min(nat.w, nat.h) < 512 && <p className="flex items-center gap-1.5 text-xs text-amber-700" data-testid="app-icon-small"><AlertTriangle className="h-3.5 w-3.5" /> {nat.w}×{nat.h} px · may look blurry on phones</p>}
          {src && nat && !square && <Cropper src={src} nat={nat} exportRef={crop} />}
          {src && square && <img src={src} alt="" className="h-28 w-28 rounded-lg border border-slate-200 object-contain" />}
          <label className="flex items-center gap-2 text-sm"><Switch checked={removeBg} onCheckedChange={setRemoveBg} data-testid="app-icon-remove-bg" /> Remove white background</label>
          <div className="space-y-1.5">
            <Label className="text-xs">Background for iOS and maskable icons</Label>
            <div className="flex flex-wrap gap-1.5">
              {[["white", "White"], ["navy", "App navy"], ["suggested", "Suggested"], ["custom", "Custom"]].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setBgMode(k)} className={cn("min-h-[36px] rounded-md border px-2.5 text-xs", bgMode === k ? "border-teal-600 bg-teal-50 font-semibold text-teal-800" : "border-slate-200")} data-testid={`app-icon-bg-${k}`}>{l}</button>
              ))}
            </div>
            {bgMode === "custom" && <Input value={custom} onChange={(e) => setCustom(e.target.value)} className="w-32" data-testid="app-icon-bg-custom" />}
            <p className="flex items-center gap-2 text-xs text-slate-500"><span className="h-4 w-4 rounded border border-slate-200" style={{ background: bg }} /> {bg}{p && ` · suggested ${p.suggested}`}</p>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Padding · {padding}%</Label><Slider min={0} max={25} step={1} value={[padding]} onValueChange={([v]) => setPadding(v)} data-testid="app-icon-padding" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Android mask preview</Label>
            <div className="flex gap-1.5">{Object.keys(SHAPES).map((k) => <button key={k} type="button" onClick={() => setShape(k)} className={cn("min-h-[36px] rounded-md border px-2.5 text-xs capitalize", shape === k ? "border-teal-600 bg-teal-50 font-semibold" : "border-slate-200")} data-testid={`app-icon-shape-${k}`}>{k === "rounded" ? "Rounded square" : k}</button>)}</div></div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" disabled={!file || !!busy} onClick={preview} data-testid="app-icon-preview">{busy === "preview" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Preview"}</Button>
            <Button disabled={!file || !p || !!busy} onClick={publish} className="bg-teal-600 hover:bg-teal-700" data-testid="app-icon-publish">{busy === "publish" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publish"}</Button>
          </div>
          {file && !p && <p className="text-xs text-slate-500">Preview first; change options and preview again before publishing.</p>}
        </section>
        <section className="rounded-lg border border-slate-200 bg-white p-4 sm:p-5">
          {p ? <Previews p={p} shape={shape} name="THE BRAIN" /> : <p className="text-sm text-slate-500" data-testid="app-icon-no-preview">Upload an image and press Preview to see the tab, home-screen and notification mocks.</p>}
        </section>
      </div>
      <section className="rounded-lg border border-slate-200 bg-white p-4 sm:p-5" data-testid="app-icon-versions">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-base font-semibold text-slate-900">Versions</h2>
          <Button size="sm" variant="outline" disabled={!st || st.active == null || !!busy} onClick={() => activate(null)} data-testid="app-icon-reset"><RotateCcw className="mr-1 h-4 w-4" /> Reset to default brain icon</Button>
        </div>
        <ul className="mt-3 divide-y divide-slate-100 text-sm">
          <li className="flex items-center justify-between gap-2 py-2"><span className="flex items-center gap-2"><img src="/brand/icon-192.png?v=brain1" alt="" className="h-7 w-7" /> Default brain icon</span>{st?.active == null && <span className="text-xs font-semibold text-teal-700">Live</span>}</li>
          {(st?.versions || []).map((x) => (
            <li key={x.v} className="flex flex-wrap items-center justify-between gap-2 py-2" data-testid={`app-icon-version-${x.v}`}>
              <span className="flex items-center gap-2">Version {x.v} · {fmtDateTime(x.at)} · {x.by} · bg {x.bg} · padding {x.padding}%</span>
              {st.active === x.v ? <span className="text-xs font-semibold text-teal-700">Live</span> : <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => activate(x.v)} data-testid={`app-icon-rollback-${x.v}`}>Make live</Button>}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
